/**
 * Super-admin editor for the onboarding guide.
 *  - Left column: guide title + ordered blocks (image, subtitle, description, links)
 *  - Right column: internal "deep-dive" pages CRUD
 *  - Media upload (image or video) goes to Emergent Object Storage
 *  - "Salva & pubblica" bumps the guide version so users re-see the popup
 */
import React, { useCallback, useEffect, useState } from "react";
import { GripVertical, Plus, Trash2, Upload, ImageIcon, VideoIcon, FileText, Save, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const uid = () => Math.random().toString(36).slice(2, 10);

const MediaField = ({ value, onChange, testid }) => {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/guide/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Media caricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload fallito");
    } finally { setBusy(false); }
  };
  const preview = value ? (
    /\.(mp4|webm|mov)$/i.test(value)
      ? <video src={value} className="w-full max-h-56 rounded-xl bg-black object-cover" controls />
      : <img src={value} alt="" className="w-full max-h-56 rounded-xl object-cover" />
  ) : (
    <div className="w-full h-40 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 text-sm">Nessun media</div>
  );
  return (
    <div className="space-y-2">
      {preview}
      <div className="flex items-center gap-2">
        <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 h-9 text-xs cursor-pointer hover:border-[#0A84FF]" data-testid={testid}>
          <Upload className="w-4 h-4" />
          {busy ? "Caricamento…" : "Carica immagine o video"}
          <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={onFile} className="hidden" disabled={busy} />
        </label>
        {value && (
          <button onClick={() => onChange("")} className="text-xs text-rose-600 hover:underline">Rimuovi</button>
        )}
      </div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…oppure incolla un URL"
        className="text-xs"
      />
    </div>
  );
};

const BlockEditor = ({ block, index, total, onChange, onMove, onDelete, pages }) => {
  const b = block;
  const set = (patch) => onChange({ ...b, ...patch });
  const setLink = (i, patch) => set({ links: (b.links || []).map((l, li) => li === i ? { ...l, ...patch } : l) });
  return (
    <div className="ios-card p-5 space-y-3" data-testid={`admin-guide-block-${index}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <GripVertical className="w-4 h-4 text-slate-300" />
          Blocco {index + 1}
        </div>
        <div className="flex items-center gap-1">
          <button className="text-xs text-slate-500 hover:text-slate-900 px-2 h-7 rounded" onClick={() => onMove(-1)} disabled={index === 0}>▲</button>
          <button className="text-xs text-slate-500 hover:text-slate-900 px-2 h-7 rounded" onClick={() => onMove(1)} disabled={index === total - 1}>▼</button>
          <button className="text-xs text-rose-600 hover:text-rose-900 ml-2" onClick={onDelete} data-testid={`admin-guide-block-${index}-delete`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <MediaField value={b.image_url || ""} onChange={(v) => set({ image_url: v })} testid={`admin-guide-block-${index}-media`} />
      <Input
        value={b.subtitle || ""}
        onChange={(e) => set({ subtitle: e.target.value })}
        placeholder="Sottotitolo"
        data-testid={`admin-guide-block-${index}-subtitle`}
      />
      <Textarea
        value={b.description || ""}
        onChange={(e) => set({ description: e.target.value })}
        placeholder="Descrizione"
        rows={4}
        data-testid={`admin-guide-block-${index}-description`}
      />
      <div className="space-y-2">
        <div className="text-xs font-semibold text-slate-600 flex items-center gap-2">
          <LinkIcon className="w-3.5 h-3.5" /> Link ad approfondimenti
        </div>
        {(b.links || []).map((l, i) => (
          <div key={i} className="flex items-center gap-2">
            <select
              value={l.slug}
              onChange={(e) => setLink(i, { slug: e.target.value })}
              className="flex-1 rounded-lg border border-slate-200 px-3 h-9 text-sm"
            >
              <option value="">— Seleziona pagina —</option>
              {pages.map((p) => <option key={p.slug} value={p.slug}>{p.title} ({p.slug})</option>)}
            </select>
            <Input value={l.label} onChange={(e) => setLink(i, { label: e.target.value })} placeholder="Etichetta" className="flex-1" />
            <button className="text-rose-600" onClick={() => set({ links: b.links.filter((_, li) => li !== i) })}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button
          onClick={() => set({ links: [...(b.links || []), { slug: "", label: "" }] })}
          className="inline-flex items-center gap-1.5 text-xs text-[#0A84FF] hover:underline"
          data-testid={`admin-guide-block-${index}-add-link`}
        >
          <Plus className="w-3.5 h-3.5" /> Aggiungi link
        </button>
      </div>
    </div>
  );
};

const AdminGuide = () => {
  const [title, setTitle] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [version, setVersion] = useState(0);
  const [pages, setPages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pageDraft, setPageDraft] = useState({ slug: "", title: "", content: "" });

  const load = useCallback(async () => {
    try {
      const g = await api.get("/guide");
      setTitle(g.data.guide.title || "");
      setBlocks(g.data.guide.blocks || []);
      setVersion(g.data.guide.version || 0);
    } catch {}
    try {
      const p = await api.get("/admin/guide/pages");
      setPages(p.data || []);
    } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const addBlock = () => setBlocks((prev) => [...prev, { id: uid(), image_url: "", subtitle: "", description: "", links: [] }]);
  const moveBlock = (idx, dir) => setBlocks((prev) => {
    const next = [...prev];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });
  const updateBlock = (idx, b) => setBlocks((prev) => prev.map((x, i) => (i === idx ? b : x)));
  const deleteBlock = (idx) => setBlocks((prev) => prev.filter((_, i) => i !== idx));

  const publish = async (bump = true) => {
    setSaving(true);
    try {
      const r = await api.put("/admin/guide", { title, blocks, bump_version: bump });
      setVersion(r.data.version);
      toast.success(bump ? `Guida pubblicata (v${r.data.version})` : "Bozza salvata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const savePage = async () => {
    if (!pageDraft.slug || !pageDraft.title) { toast.error("Slug e titolo obbligatori"); return; }
    try {
      await api.put("/admin/guide/pages", pageDraft);
      toast.success("Pagina salvata");
      setPageDraft({ slug: "", title: "", content: "" });
      const p = await api.get("/admin/guide/pages");
      setPages(p.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio pagina"); }
  };
  const editPage = (p) => setPageDraft({ slug: p.slug, title: p.title, content: p.content || "" });
  const deletePage = async (slug) => {
    if (!window.confirm(`Eliminare la pagina "${slug}"?`)) return;
    try {
      await api.delete(`/admin/guide/pages/${slug}`);
      const p = await api.get("/admin/guide/pages");
      setPages(p.data || []);
      toast.success("Pagina eliminata");
    } catch { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="admin-guide-panel">
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Guida utenti</div>
            <h2 className="font-display font-bold text-xl tracking-tight mt-1">Contenuti del popup</h2>
            <p className="text-xs text-slate-500 mt-1">Versione attuale pubblicata: <b>v{version || "—"}</b></p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => publish(false)} disabled={saving} className="rounded-full" data-testid="admin-guide-save-draft">
              <Save className="w-4 h-4 mr-1.5" /> Salva bozza
            </Button>
            <Button onClick={() => publish(true)} disabled={saving} className="rounded-full bg-[#0A84FF]" data-testid="admin-guide-publish">
              Pubblica nuova versione
            </Button>
          </div>
        </div>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo della guida (es. Benvenuto in ImmoMap)" data-testid="admin-guide-title" />

        {blocks.map((b, i) => (
          <BlockEditor
            key={b.id || i}
            block={b}
            index={i}
            total={blocks.length}
            onChange={(nb) => updateBlock(i, nb)}
            onMove={(d) => moveBlock(i, d)}
            onDelete={() => deleteBlock(i)}
            pages={pages}
          />
        ))}

        <button
          onClick={addBlock}
          className="w-full rounded-2xl border-2 border-dashed border-slate-200 hover:border-[#0A84FF] hover:text-[#0A84FF] transition py-6 text-slate-500 font-medium flex items-center justify-center gap-2"
          data-testid="admin-guide-add-block"
        >
          <Plus className="w-5 h-5" /> Aggiungi blocco
        </button>
      </div>

      <div className="space-y-4">
        <div className="ios-card p-5 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Pagine di approfondimento</div>
            <h3 className="font-display font-bold text-lg tracking-tight mt-1 flex items-center gap-2"><FileText className="w-4 h-4" /> Editor pagine</h3>
          </div>
          <Input value={pageDraft.slug} onChange={(e) => setPageDraft({ ...pageDraft, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="slug-univoco" data-testid="admin-guide-page-slug" />
          <Input value={pageDraft.title} onChange={(e) => setPageDraft({ ...pageDraft, title: e.target.value })} placeholder="Titolo pagina" data-testid="admin-guide-page-title" />
          <Textarea value={pageDraft.content} onChange={(e) => setPageDraft({ ...pageDraft, content: e.target.value })} placeholder="Contenuto (supporta accapo)" rows={8} data-testid="admin-guide-page-content" />
          <Button onClick={savePage} className="rounded-full bg-[#0A84FF] w-full" data-testid="admin-guide-page-save">Salva pagina</Button>
        </div>

        <div className="ios-card p-5 space-y-2">
          <div className="text-sm font-semibold text-slate-700 mb-2">Pagine esistenti ({pages.length})</div>
          {pages.length === 0 && <div className="text-xs text-slate-400">Nessuna pagina.</div>}
          {pages.map((p) => (
            <div key={p.slug} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 border border-slate-100">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{p.title}</div>
                <div className="text-[11px] text-slate-400 truncate">{p.slug}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => editPage(p)} className="text-xs text-[#0A84FF] hover:underline">Modifica</button>
                <button onClick={() => deletePage(p.slug)} className="text-rose-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminGuide;
